import React, { useEffect, useState } from "react";
import {
  Button,
  TableContainer,
  Paper,
  TextField,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";

import { AddCircleSharp } from "@mui/icons-material";

import { toast } from "react-toastify";
import { showConfirmation } from "../../utils/SwalHelper";
import { AddressData } from "../../types/Address";
import {
  createData,
  getData,
  updateData,
  deleteData,
} from "../../api/EmergencyContact";
import {
  EmergencyContactData,
  initialEmergencyContactData,
} from "../../types/EmergencyContact";
import EmergencyContactForm from "./EmergencyContactForm";
import { GenericTable } from "../../components/common/GenericTable";
import { contactTableColumns } from "../patients/tables/TableHeader";
import { UserData } from "../../types/User";

type ComponentProps = {
  detailData: UserData;
};

const EmergencyContactList: React.FC<ComponentProps> = ({ detailData }) => {
  const [inputData, setInputData] = useState<EmergencyContactData>(
    initialEmergencyContactData
  );
  const [listData, setListData] = useState<EmergencyContactData[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);

    const resp = await getData(detailData.id);
    setListData(resp);

    setLoading(false);
  };

  const handleAdd = () => {
    setInputData(initialEmergencyContactData);
    setIsEdit(false);
    setOpen(true);
  };

  const handleEdit = async (data: EmergencyContactData) => {
    setIsEdit(true);

    await setInputData(data);

    setOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const confirmed = await showConfirmation(
        "Are you sure?",
        "Do you want to delete this record permanently?",
        "warning",
        "Yes, delete it!"
      );

      if (confirmed) {
        const resp = await deleteData(id);
        if (!resp) {
          toast.error("Failed to delete record");
          return;
        }
        toast.success("Record deleted successfully");
        await loadData();
      }
    } catch (error: any) {
      console.error("Delete failed:", error);
      toast.error(error.response?.data?.message || "Error deleting record");
    }
  };

  const onSubmit = async (form: Partial<AddressData>) => {
    setIsEdit(false);
    // alert(JSON.stringify(form));
    if (form.id) {
      const resp = await updateData(form.id, form);
      if (resp == null) {
        toast.error("Failed to save address.");
        return;
      }

      setOpen(false);
      await loadData();
      toast.success("Emergency contact successfully updated");
    } else {
      // alert(JSON.stringify(form));
      const resp = await createData(form);
      if (resp == null) {
        toast.error("Failed to save address.");
        return;
      }
      setOpen(false);
      await loadData();
      toast.success("Emergency contact successfully created");
    }
  };

  const filteredData = listData.filter((data) => {
    const search = searchTerm.toLowerCase();
    return (
      (data.name?.toLowerCase().includes(search) ?? false) ||
      (data.primaryPhone?.toLowerCase().includes(search) ?? false) ||
      (data.secondaryPhone?.toLowerCase().includes(search) ?? false) ||
      (data.email?.toLowerCase().includes(search) ?? false)
    );
  });

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div>
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <TextField
            label="Search data"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 300 }}
          />
          <Button startIcon={<AddCircleSharp />} onClick={handleAdd}></Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
            <GenericTable
              data={listData}
              columns={contactTableColumns}
              actions={{
                edit: (row) => handleEdit(row),
                delete: (row) => handleDelete(row.id),
              }}
            />
          </TableContainer>
        )}

        <Dialog
          open={open}
          onClose={(event, reason) => {
            setInputData(initialEmergencyContactData);
            if (reason === "backdropClick" || reason === "escapeKeyDown")
              return;
            setOpen(false);
          }}
          disableEscapeKeyDown
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {isEdit ? "Edit Emergency Contact" : "Add Emergency Contact"}
          </DialogTitle>
          <DialogContent>
            <EmergencyContactForm
              onSubmit={onSubmit}
              detailData={detailData}
              initialData={inputData || initialEmergencyContactData}
              onClose={() => setOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </Box>
    </div>
  );
};

export default EmergencyContactList;
